import React, { useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TransactionsTable = ({ transactions, onViewDetails, onGenerateInvoice }) => {
  const { currentLanguage, t } = useLanguage();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const formatCurrency = (amount) => {
    if (currentLanguage === 'ar') {
      // Arabic formatting with Arabic numerals
      const formattedNumber = new Intl.NumberFormat('ar-MA', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })?.format(amount);
      return `${formattedNumber} ${t('financial.currency')}`;
    } else {
      // French formatting
      return new Intl.NumberFormat('fr-MA', {
        style: 'currency',
        currency: 'MAD',
        minimumFractionDigits: 2
      })?.format(amount);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    // Always use en-US locale for universal date format (Western numerals)
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })?.format(date);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: {
        label: t('financial.completed'),
        className: 'bg-success/10 text-success border-success/20'
      },
      pending: {
        label: t('financial.pending'),
        className: 'bg-warning/10 text-warning border-warning/20'
      },
      overdue: {
        label: t('financial.overdue'),
        className: 'bg-error/10 text-error border-error/20'
      },
      refunded: {
        label: t('financial.refunded'),
        className: 'bg-primary/10 text-primary border-primary/20'
      }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;

    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${config?.className}`}>
        {config?.label}
      </span>
    );
  };

  const totalPages = Math.ceil(transactions?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTransactions = transactions?.slice(startIndex, endIndex);

  return (
    <div className="bg-card rounded-lg border border-border shadow-warm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left caption font-semibold text-foreground">
                {t('financial.pilgrimName')}
              </th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left caption font-semibold text-foreground">
                {t('financial.amount')}
              </th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left caption font-semibold text-foreground hidden md:table-cell">
                {t('financial.type')}
              </th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left caption font-semibold text-foreground hidden lg:table-cell">
                {t('financial.method')}
              </th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left caption font-semibold text-foreground">
                {t('financial.status')}
              </th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left caption font-semibold text-foreground hidden xl:table-cell">
                {t('financial.dueDate')}
              </th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-right caption font-semibold text-foreground">
                {t('financial.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {currentTransactions?.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 md:px-6 py-12 text-center">
                  <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">
                    {t('financial.noTransactions')}
                  </p>
                </td>
              </tr>
            ) : (
              currentTransactions?.map((transaction) => (
                <tr key={transaction?.id} className="hover:bg-muted/50 transition-smooth">
                  <td className="px-4 md:px-6 py-3 md:py-4">
                    <div>
                      <p className="text-sm font-medium text-foreground">{transaction?.pilgrimName}</p>
                      <p className="caption text-muted-foreground">{transaction?.pilgrimId}</p>
                    </div>
                  </td>
                  <td className="px-4 md:px-6 py-3 md:py-4">
                    <p className="text-sm font-semibold text-foreground data-text">
                      {formatCurrency(transaction?.amount)}
                    </p>
                  </td>
                  <td className="px-4 md:px-6 py-3 md:py-4 hidden md:table-cell">
                    <p className="caption text-muted-foreground">{transaction?.type}</p>
                  </td>
                  <td className="px-4 md:px-6 py-3 md:py-4 hidden lg:table-cell">
                    <p className="caption text-muted-foreground">{transaction?.method}</p>
                  </td>
                  <td className="px-4 md:px-6 py-3 md:py-4">
                    {getStatusBadge(transaction?.status)}
                  </td>
                  <td className="px-4 md:px-6 py-3 md:py-4 hidden lg:table-cell">
                    <p className="caption text-muted-foreground">
                      {transaction?.dueDate ? formatDate(transaction?.dueDate) : '-'}
                    </p>
                  </td>
                  <td className="px-4 md:px-6 py-3 md:py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Eye"
                        onClick={() => onViewDetails(transaction)}
                      >
                        {t('financial.viewDetails')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="FileText"
                        onClick={() => onGenerateInvoice(transaction)}
                      >
                        {t('financial.generateInvoiceAction')}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {transactions?.length > itemsPerPage && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 md:px-6 py-4 border-t border-border">
          <p className="caption text-muted-foreground">
            {t('financial.showing')} {startIndex + 1}-{Math.min(endIndex, transactions?.length)} {t('financial.of')} {transactions?.length} {t('financial.transactions')}
          </p>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronLeft"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            />
            
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronRight"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionsTable;